import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMantenimiento() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // ── ESTADO DEL SISTEMA ──
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const cargarStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const r = await axios.get(`${API}/admin/system-status`);
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar estado");
    } finally { setLoadingStatus(false); }
  }, []);

  useEffect(() => { cargarStatus(); }, [cargarStatus]);

  // ── LIMPIAR DATOS DE PRUEBA ──
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [borrando, setBorrando] = useState(false);

  const cargarPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await axios.post(`${API}/admin/reset-test-data`, { confirmar: false });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al previsualizar");
    } finally { setLoadingPreview(false); }
  };

  const ejecutarBorrado = async () => {
    setBorrando(true);
    try {
      const r = await axios.post(`${API}/admin/reset-test-data`, { confirmar: true });
      toast.success(`Eliminados ${r.data.total_borrados} registros de prueba`);
      setConfirmando(false);
      setPreview(null);
      cargarStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al borrar datos");
    } finally { setBorrando(false); }
  };

  return (
    <div className="hub-bg" data-testid="page-mantenimiento">
      <header className="topnav">
        <div className="brand">
          <button onClick={() => navigate("/hub")} className="iconbtn mr-1" aria-label="Atrás" data-testid="btn-back-hub">
            <i className="fa-solid fa-arrow-left"/>
          </button>
          <div className="logo" style={{background:"#0F172A"}}><i className="fa-solid fa-screwdriver-wrench"/></div>
          <div>
            <div className="title">Mantenimiento del sistema</div>
            <div className="sub">DMU 614 · Panel de administración</div>
          </div>
        </div>
        <div className="actions">
          <div className="hidden sm:block text-right mr-2">
            <div className="text-sm font-semibold leading-tight">{user?.nombre}</div>
            <div className="text-xs text-slate-500">{user?.rol}</div>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="btn-logout-mant" style={{padding:"0.5rem 0.9rem"}}>
            <i className="fa-solid fa-right-from-bracket mr-1"/>Salir
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {/* ── ESTADO DEL SISTEMA ── */}
        <section className="card-base" data-testid="seccion-status">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold m-0">
              <i className="fa-solid fa-heart-pulse mr-2 text-emerald-600"/>Estado del sistema
            </h2>
            <button className="btn-ghost text-sm" onClick={cargarStatus} disabled={loadingStatus} data-testid="btn-refresh-status">
              <i className={`fa-solid fa-rotate ${loadingStatus ? "fa-spin":""} mr-1`}/>Actualizar
            </button>
          </div>
          {!status ? (
            <div className="text-slate-400 text-sm">Cargando…</div>
          ) : (
            <>
              <div className={`rounded-lg p-3 mb-3 ${status.ok_global ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900"}`} data-testid="status-global">
                <i className={`fa-solid ${status.ok_global ? "fa-circle-check":"fa-circle-xmark"} mr-1`}/>
                <strong>{status.ok_global ? "Todos los checks pasan" : "Hay checks fallando"}</strong>
                <span className="text-xs ml-2 opacity-70">Última verificación: {new Date(status.generado_en).toLocaleString("es-MX")}</span>
              </div>
              <ul className="space-y-2">
                {status.checks.map((c) => (
                  <li key={c.id} className="flex items-start gap-3 p-2 rounded-md border border-slate-200" data-testid={`check-${c.id}`}>
                    <div className="text-xl mt-0.5">
                      {c.ok ? <span className="text-emerald-600">✅</span> : <span className="text-red-600">❌</span>}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{c.label}</div>
                      <div className="text-xs text-slate-500">{c.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        {/* ── LIMPIAR DATOS DE PRUEBA ── */}
        <section className="card-base" data-testid="seccion-reset">
          <h2 className="text-lg font-bold m-0 mb-1">
            <i className="fa-solid fa-broom mr-2 text-red-600"/>Limpiar datos de prueba
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            Borra <strong>solo datos operativos</strong> generados durante pruebas: servicios, intervenciones, eventos, solicitudes, tickets, validaciones, coberturas, suspensiones y logs.<br/>
            <strong className="text-emerald-700">NO toca:</strong> usuarios, choferes, supervisores, pipas, zonas, espacios públicos, rutas ni configuración.
          </p>

          {!preview && !confirmando && (
            <button className="btn-primary" onClick={cargarPreview} disabled={loadingPreview} data-testid="btn-previsualizar">
              {loadingPreview ? "Calculando…" : (<><i className="fa-solid fa-eye mr-1"/>Previsualizar qué se borrará</>)}
            </button>
          )}

          {preview && !confirmando && (
            <>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-3 text-sm">
                <div className="font-semibold text-amber-900 mb-2">Se borrarán {preview.total_borrados ?? Object.values(preview.conteos).reduce((s,v)=>s+(v.se_borrarian||v.borrados||0),0)} registros:</div>
                <ul className="text-xs space-y-1">
                  {Object.entries(preview.conteos).filter(([_,v]) => (v.se_borrarian||v.borrados||0) > 0).map(([col, v]) => (
                    <li key={col} className="flex justify-between">
                      <span className="font-mono text-slate-700">{col}</span>
                      <strong className="text-amber-900">{v.se_borrarian || v.borrados || 0}</strong>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 mb-3 text-xs">
                <strong className="text-emerald-900">Colecciones PROTEGIDAS (no se tocan):</strong>{" "}
                <span className="font-mono text-emerald-800">{preview.protegidas.join(", ")}</span>
              </div>
              <div className="flex gap-2">
                <button className="btn-ghost" onClick={()=>setPreview(null)} data-testid="btn-cancelar-preview">Cancelar</button>
                <button className="btn-primary" style={{background:"#DC2626"}} onClick={()=>setConfirmando(true)} data-testid="btn-confirmar-borrado">
                  <i className="fa-solid fa-triangle-exclamation mr-1"/>Continuar al borrado…
                </button>
              </div>
            </>
          )}

          {confirmando && (
            <div className="rounded-lg bg-red-50 border-2 border-red-300 p-4" data-testid="confirm-dialog">
              <div className="text-lg font-bold text-red-900 mb-2">
                <i className="fa-solid fa-circle-exclamation mr-1"/>¿Borrar todos los registros de prueba?
              </div>
              <div className="text-sm text-red-800 mb-4">
                Esta acción <strong>no se puede deshacer</strong>. Se eliminarán <strong>{preview?.total_borrados ?? Object.values(preview?.conteos||{}).reduce((s,v)=>s+(v.se_borrarian||0),0)}</strong> registros operativos. Los usuarios, pipas, zonas y rutas se mantendrán intactos.
              </div>
              <div className="flex gap-2">
                <button className="btn-ghost" onClick={()=>{setConfirmando(false);}} disabled={borrando} data-testid="btn-cancelar-final">Cancelar</button>
                <button className="btn-primary" style={{background:"#DC2626"}} onClick={ejecutarBorrado} disabled={borrando} data-testid="btn-borrar-definitivo">
                  {borrando ? "Borrando…" : (<><i className="fa-solid fa-trash mr-1"/>Sí, borrar definitivamente</>)}
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="text-xs text-slate-400 text-center pb-4">
          DMU 614 · Mantenimiento solo para rol <span className="mono">admin</span> · Las acciones quedan registradas en el audit log.
        </section>
      </main>
    </div>
  );
}
